import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import AuthRoutes from "Routes/Components/Auth";

import { HTTPErrorCallback } from "Helpers/Error";
import {
  documentExtensions,
  imageExtensions,
  spreadsheetExtensions,
} from "Helpers/File";

import UseFile from "Hooks/UseFile";
import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import FormWrapper from "Components/Common/FormWrapper";
import Button from "Components/Global/Buttons";
import Input from "Components/Global/Form/Input";

import DefaultForm from "../../Components/DefaultForm";
import * as S from "./styles";

// Documentos obrigatórios da Etapa 2 (mesma regra na criação e na edição).
// "Outros" (other_src) segue opcional.
const REQUIRED_DOCUMENTS = [
  { key: "cnpj_src", label: "Cartão de CNPJ" },
  { key: "approval_src", label: "Documento de aprovação do projeto (DOU)" },
  { key: "registered_project_src", label: "Projeto conforme inscrito na lei" },
  { key: "project_budget_src", label: "Planilha orçamentária do projeto" },
  { key: "caption_book_src", label: "Book de captação" },
];

const SecondForm = ({
  setStep,
  projectId,
  projectData,
  setProjectData,
  isUpdate = false,
  disabled,
  isDownload,
  reload,
}) => {
  const navigate = useNavigate();

  const { api } = UseServices();
  const { setLoading } = UseGlobalLoading();
  const { showNotification } = UseNotification();
  const { uploadFile } = UseFile();

  const [srcData, setSrcData] = useState(projectData?.srcData || {});

  useEffect(() => {
    window.scrollTo(0, 1200);
  }, []);

  const methods = useForm({ defaultValues: projectData });
  const { register } = methods;

  const submit = async (data) => {
    const missingDocuments = REQUIRED_DOCUMENTS.filter(
      ({ key }) => !srcData[key]?.length,
    );

    if (missingDocuments.length) {
      showNotification(
        "danger",
        "Documentos obrigatórios",
        `Envie os documentos obrigatórios: ${missingDocuments
          .map(({ label }) => label)
          .join(", ")}.`,
      );
      return;
    }

    try {
      data.step = 3;
      data.project_id = projectId;

      setLoading(true);

      for (const key in srcData) {
        if (
          srcData[key] &&
          Array.isArray(srcData[key]) &&
          srcData[key]?.length
        ) {
          for (const file of srcData[key]) {
            if (!data[key]) data[key] = [];

            if (file?.id) {
              data[key].push(file);
              continue;
            }

            const image_location = await uploadFile(file);

            data[key].push({
              file_name: file.name,
              file_src: image_location,
            });
          }
        }
      }

      data.is_update = true;

      await api.post("/projects/submit-project", data);

      setProjectData({
        ...projectData,
        ...data,
        srcData,
      });

      showNotification(
        "success",
        "Sucesso!",
        `Seu projeto foi ${isUpdate ? "atualizado" : "enviado"} com sucesso!`,
      );

      if (reload) reload(projectId);

      if (!isUpdate) navigate(AuthRoutes.Dashboard.path);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      HTTPErrorCallback(error);
    }
  };

  return (
    <FormWrapper {...methods} submit={submit}>
      <S.Wrapper>
        {!isDownload && (
          <DefaultForm name="Documentos">
            <S.Row>
              <Input
                {...register("cnpj_src")}
                placeholder="Enviar do computador"
                label="Cartão de CNPJ (últimos 3 meses):"
                type="file"
                value={srcData.cnpj_src || []}
                onChange={(e) => setSrcData({ ...srcData, cnpj_src: e })}
                allowedExtensions={[...imageExtensions, ...documentExtensions]}
                required
                disabled={disabled}
                allowMultiple
              />

              <Input
                {...register("approval_src")}
                placeholder="Enviar do computador"
                label="Documento de aprovação do projeto (DOU - APROVAÇÃO / PRORROGAÇÃO):"
                type="file"
                value={srcData.approval_src || []}
                onChange={(e) => setSrcData({ ...srcData, approval_src: e })}
                allowedExtensions={[...imageExtensions, ...documentExtensions]}
                required
                disabled={disabled}
                allowMultiple
              />
            </S.Row>
            <S.Row>
              <Input
                {...register("registered_project_src")}
                placeholder="Enviar do computador"
                label="Projeto conforme inscrito na lei
                (pdf do projeto inscrito):"
                type="file"
                value={srcData.registered_project_src || []}
                onChange={(e) =>
                  setSrcData({ ...srcData, registered_project_src: e })
                }
                allowedExtensions={[...imageExtensions, ...documentExtensions]}
                required
                disabled={disabled}
                allowMultiple
              />
            </S.Row>
            <S.Row>
              <Input
                {...register("project_budget_src")}
                placeholder="Enviar do computador"
                label="Planilha orçamentária do projeto:"
                type="file"
                value={srcData.project_budget_src || []}
                onChange={(e) =>
                  setSrcData({ ...srcData, project_budget_src: e })
                }
                allowedExtensions={[
                  ...imageExtensions,
                  ...documentExtensions,
                  ...spreadsheetExtensions,
                ]}
                required
                disabled={disabled}
                allowMultiple
              />

              <Input
                {...register("caption_book_src")}
                placeholder="Enviar do computador"
                label="Book de captação (pdf de apresentação comercial do projeto):"
                type="file"
                value={srcData.caption_book_src || []}
                onChange={(e) =>
                  setSrcData({ ...srcData, caption_book_src: e })
                }
                allowedExtensions={[...imageExtensions, ...documentExtensions]}
                required
                disabled={disabled}
                allowMultiple
              />
            </S.Row>
            <S.Row>
              <Input
                {...register("other_src")}
                placeholder="Enviar do computador"
                label="Outros documentos:"
                type="file"
                value={srcData.other_src || []}
                onChange={(e) => setSrcData({ ...srcData, other_src: e })}
                allowedExtensions={[...imageExtensions, ...documentExtensions]}
                disabled={disabled}
                allowMultiple
              />
            </S.Row>
          </DefaultForm>
        )}

        {!isDownload && (
          <S.Buttons>
            <Button
              type="button"
              outline
              text="Voltar"
              onClick={() => setStep(0)}
            />

            {!disabled ? <Button type="submit" text="Finalizar" /> : null}
          </S.Buttons>
        )}
      </S.Wrapper>
    </FormWrapper>
  );
};

export default SecondForm;
